import { Theme } from './types';

export const classicTheme: Theme = {
  id: 'classic',
  name: 'Classic QuizCrunch',
  description: 'Bold and vibrant quiz experience with iconic colors and smooth gameplay',

  colors: {
    choice1: 'linear-gradient(135deg, #E2184A 0%, #D0144D 100%)', // Vibrant Red (Triangle)
    choice2: 'linear-gradient(135deg, #1368CE 0%, #0F5CB8 100%)', // Bold Blue (Diamond)
    choice3: 'linear-gradient(135deg, #FFA602 0%, #F59600 100%)', // Bright Orange (Circle)
    choice4: 'linear-gradient(135deg, #26890C 0%, #1E7209 100%)', // Rich Green (Square)

    correct: '#26890C',
    incorrect: '#E2184A',

    background: 'linear-gradient(135deg, #46178F 0%, #7B2CBF 100%)', // Purple gradient
    cardBackground: 'rgba(0, 0, 0, 0.5)', // Darker semi-transparent background for better contrast
    textPrimary: '#ffffff', // Pure white for maximum contrast
    textSecondary: '#e0e0e0', // Light gray for secondary text
    accent: '#FFD700', // Brighter gold for achievement highlights

    timerHigh: '#26890C',
    timerMid: '#FFA602',
    timerLow: '#E2184A',

    // Semantic colors for consistent UI
    surface: 'rgba(255, 255, 255, 0.1)', // Light transparent for glass effect
    surfaceVariant: 'rgba(255, 255, 255, 0.15)', // Slightly more opaque variant
    onSurface: '#ffffff', // Text on surface backgrounds
    onSurfaceVariant: '#e0e0e0', // Secondary text on surface
    outline: 'rgba(255, 255, 255, 0.3)', // Border colors
    outlineVariant: 'rgba(255, 255, 255, 0.15)', // Subtle borders
    scrim: 'rgba(0, 0, 0, 0.6)', // Overlay backgrounds

    // Interactive element colors
    primary: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)', // Purple to pink gradient
    onPrimary: '#ffffff', // Text on primary buttons
    secondary: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)', // Blue to green gradient
    onSecondary: '#ffffff', // Text on secondary buttons

    // Status colors
    success: '#10B981', // Green for success
    warning: '#FFA602', // Orange for warnings
    error: '#E2184A', // Red for errors
    info: '#3B82F6', // Blue for info
  },

  typography: {
    fontFamily: "'Poppins', 'Inter', system-ui, sans-serif",
    // Legacy sizes (keep for backward compatibility)
    questionSize: '2xl',
    answerSize: 'lg',
    scoreSize: 'base',

    // New systematic size scale
    sizes: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
    },

    // Line heights
    lineHeights: {
      tight: 'leading-tight',
      normal: 'leading-normal',
      relaxed: 'leading-relaxed',
    },

    // Font weights
    weights: {
      normal: 'font-normal',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },

  animations: {
    buttonHover: 'hover:scale-105 transition-transform duration-200',
    buttonPress: 'scale-110 ring-4 ring-white',
    correctAnswer: 'animate-pulse',
    wrongAnswer: 'shake-crazy',
    transition: 'transition-all duration-200',
  },

  emojis: {
    choice1: '▲',
    choice2: '◆',
    choice3: '●',
    choice4: '■',
    score: '★',
    timer: '⏱',
    correct: '✓',
    incorrect: '✗',
  },

  audio: {
    backgroundMusic: 'https://quizcrunch-prod-play-static-website-962595531541.s3.us-east-2.amazonaws.com/media/classic/classic.mp3',
    enabled: true,
  },

  effects: {
    particles: true,
    glassmorphism: false,
    buttonShadows: true,
    confetti: true,
    screenShake: true,
  },

  layout: {
    buttonShape: 'rounded',
    buttonArrangement: 'grid',
    spacing: 'normal',
  },

  spacing: {
    xs: '0.5rem',  // 8px
    sm: '1rem',    // 16px
    md: '1.5rem',  // 24px
    lg: '2rem',    // 32px
    xl: '3rem',    // 48px
    '2xl': '4rem', // 64px
  },

  elevation: {
    none: 'shadow-none',
    sm: 'shadow-md',      // Subtle shadow (cards at rest)
    md: 'shadow-lg',      // Medium shadow (raised elements)
    lg: 'shadow-xl',      // Large shadow (floating elements)
    xl: 'shadow-2xl',     // Extra large shadow (modals, overlays)
  },

  borderRadius: {
    sm: '0.5rem',   // 8px - Small elements
    md: '1rem',     // 16px - Buttons, inputs
    lg: '1.5rem',   // 24px - Cards
    full: '9999px', // Fully rounded
  },
};